import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import { DeleteItem, SuccessModal, InviteModal } from "../../components/modal";
import InfoCard from "../../components/card/infocard";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { AVAILABLE_MODULES } from "../../data/modules";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const PERMISSION_DETAILS_CACHE_KEY_PREFIX = "cehr_permission_details_v1";

const PermissionViewDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissionIdFromQuery = new URLSearchParams(location.search).get("permissionId");
  const permissionId = location.state?.permissionId || permissionIdFromQuery;
  const cacheKey = useMemo(() => `${PERMISSION_DETAILS_CACHE_KEY_PREFIX}_${permissionId || "unknown"}`, [permissionId]);
  const cachedData = useMemo(() => readSessionCache(cacheKey, { permission: null, positions: [] }), [cacheKey]);

  const [permission, setPermission] = useState(cachedData?.permission || null);
  const [positions, setPositions] = useState(cachedData?.positions || []);
  const [isLoading, setIsLoading] = useState(!cachedData?.permission);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [opensuccessModal, setOpensuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    positionId: "",
    permissionName: "",
    description: "",
    components: [],
  });

  const [title, setTitle] = useState("Permission");

  useEffect(() => {
    if (permissionId) {
      loadPermissionDetails();
      loadPositions();
    }
  }, [permissionId]);

  const loadPermissionDetails = async () => {
    if (!permission) {
      setIsLoading(true);
    }
    try {
      const response = await axios.get(`${getApiBaseUrl()}/organizations/permissions/${permissionId}`);
      if (response.data && response.data.permission) {
        const perm = response.data.permission;
        setPermission(perm);
        setTitle(perm.permissionName);
        setFormData({
          positionId: perm.positionId,
          permissionName: perm.permissionName,
          description: perm.description,
          components: Array.isArray(perm.components) ? perm.components : perm.component ? [perm.component] : [],
        });
        writeSessionCache(cacheKey, { permission: perm, positions });
      }
    } catch (error) {
      console.error("Error loading permission details:", error);
      setErrorMessage("Failed to load permission details");
    } finally {
      setIsLoading(false);
    }
  };

  const loadPositions = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/organizations/positions`);
      if (response.data && response.data.positions) {
        const nextPositions = response.data.positions;
        setPositions(nextPositions);
        writeSessionCache(cacheKey, { permission, positions: nextPositions });
      }
    } catch (error) {
      console.error("Error loading positions:", error);
    }
  };

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPermissionDetails();
        loadPositions();
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [permissionId, permission, positions]);

  const toggleComponentSelection = (componentId) => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.includes(componentId)
        ? prev.components.filter((c) => c !== componentId)
        : [...prev.components, componentId],
    }));
  };

  const getPositionName = (positionId) => {
    const position = positions.find((p) => p.id === positionId);
    return position ? position.positionName : "Unknown Position";
  };

  const getModuleLabels = () => {
    return formData.components
      .map((id) => AVAILABLE_MODULES.find((m) => m.id === id)?.label || id)
      .join(", ");
  };

  const getModuleLabelsList = () => {
    return formData.components.map((id) => AVAILABLE_MODULES.find((m) => m.id === id)?.label || id);
  };

  const createdAt = permission?.createdAt
    ? new Date(permission.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  const handleSave = async (ev) => {
    ev.preventDefault();
    setFormErrorMessage("");

    if (!formData.positionId || !formData.permissionName.trim() || !formData.description.trim() || formData.components.length === 0) {
      setFormErrorMessage("All fields are required, and at least one module must be selected");
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.patch(
        `${getApiBaseUrl()}/organizations/permissions/${permissionId}`,
        formData
      );

      if (response.data && response.data.permission) {
        setPermission(response.data.permission);
        setTitle(response.data.permission.permissionName);
        setSuccessMessage("Permission updated successfully");
        setOpenEditModal(false);
        setOpensuccessModal(true);
      }
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update permission. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await axios.delete(`${getApiBaseUrl()}/organizations/permissions/${permissionId}`);
      setSuccessMessage("Permission deleted successfully");
      setOpenDeleteModal(false);
      setOpensuccessModal(true);
      setTimeout(() => {
        navigate("/company-settings/permission");
      }, 2000);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Unable to delete permission");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Permissions", path: "/company-settings/permission" },
          { label: title },
        ]}
        title={title}
        subtitle="View and manage permission details."
        fallbackPath="/company-settings/permission"
        backLabel="Back to Permissions"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setFormErrorMessage("");
                setOpenEditModal(true);
              }}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={() => setOpenDeleteModal(true)}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !permission ? (
        <FetchLoadingState message="Loading permission details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !permission ? (
        <div className="lg:px-6 my-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && permission ? (
        <InfoCard
          details="Permission Details"
          title={permission.permissionName}
          description={permission.description}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Position", value: getPositionName(permission.positionId) },
            {
              label: "Modules/Components",
              value: (
                <div className="flex flex-wrap gap-2">
                  {getModuleLabelsList().map((label, idx) => (
                    <span
                      key={idx}
                      className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm font-medium"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              ),
            },
          ]}
        />
      ) : null}

      {/* Edit Modal */}
      <InviteModal isOpen={openEditModal} onClose={() => setOpenEditModal(false)}>
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Edit Permission</p>
          <form className="my-8" onSubmit={handleSave}>
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div className="mb-4">
              <label className="block mb-2 font-[500] text-[14px] text-black">Position</label>
              <select
                value={formData.positionId}
                onChange={(e) => setFormData({ ...formData, positionId: e.target.value })}
                className="w-full p-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5]"
              >
                <option value="">Select a position</option>
                {positions.map((pos) => (
                  <option key={pos.id} value={pos.id}>
                    {pos.positionName}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 font-[500] text-[14px] text-black">Permission Name</label>
              <input
                type="text"
                value={formData.permissionName}
                onChange={(e) => setFormData({ ...formData, permissionName: e.target.value })}
                className="w-full p-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5]"
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 font-[500] text-[14px] text-black">Select Modules/Components</label>
              <p className="text-sm text-gray-600 mb-3">Select all modules this position should have access to:</p>
              <div className="grid grid-cols-2 gap-3 p-4 border-2 border-[#F3F5F5] rounded-xl max-h-80 overflow-y-auto">
                {AVAILABLE_MODULES.map((module) => (
                  <label key={module.id} className="flex items-center gap-2 text-sm text-black cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.components.includes(module.id)}
                      onChange={() => toggleComponentSelection(module.id)}
                      className="w-4 h-4 rounded"
                    />
                    <span>{module.label}</span>
                  </label>
                ))}
              </div>
              {formData.components.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-[#1234CD]">
                  Selected: {formData.components.length} module(s)
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block mb-2 font-[500] text-[14px] text-black">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] resize-none"
                rows="4"
              />
            </div>

            <div className="flex justify-center gap-12 mt-10 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      {/* Delete Modal */}
      <DeleteItem isOpen={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <div>
          <img className="w-40 mx-auto animate-pulse" src={DeleteImg} alt="delete permission" />
          <h1 className="text-lg text-center text-[#000000] font-bold">Continue to delete this permission?</h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={() => setOpenDeleteModal(false)}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      {/* Success Modal */}
      <SuccessModal
        isOpen={opensuccessModal}
        onClose={() => setOpensuccessModal(false)}
      >
        <div className="flex flex-col items-center gap-4">
          <img src={verifyImg} alt="Success" className="w-16 h-16" />
          <p className="text-lg font-semibold text-green-600">{successMessage}</p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default PermissionViewDetails;
